import Phaser from "phaser";
import { Client, Room } from "colyseus.js";
import { getColyseusEndpoint } from "../colyseus/endpoint";
import {
  ROOM_NAME_ARENA,
  WALLS,
  VAULTS,
  WORLD_W,
  WORLD_H,
  getFighter,
  PLAYER_RADIUS,
  PROJECTILE_RADIUS,
  PICKUP_RADIUS,
} from "@circuit/shared";

export class ArenaScene extends Phaser.Scene {
  private room: Room | null = null;
  private sessionId: string | null = null;
  private sendAcc = 0;
  private fireEdge = false;
  private superEdge = false;
  private pointerDown = false;
  private keys!: {
    w: Phaser.Input.Keyboard.Key;
    a: Phaser.Input.Keyboard.Key;
    s: Phaser.Input.Keyboard.Key;
    d: Phaser.Input.Keyboard.Key;
    e: Phaser.Input.Keyboard.Key;
  };
  private playerSprites = new Map<string, Phaser.GameObjects.Container>();
  private projSprites = new Map<string, Phaser.GameObjects.Arc>();
  private pickupSprites = new Map<string, Phaser.GameObjects.Arc>();

  constructor() {
    super("ArenaScene");
  }

  async create() {
    document.getElementById("hud")?.classList.remove("hidden");
    if ("ontouchstart" in window) {
      document.querySelector("[data-testid='touch-hint']")?.classList.remove("hidden");
    }

    const bg = this.add.graphics();
    bg.fillStyle(0x0c0e18, 1);
    bg.fillRect(0, 0, WORLD_W, WORLD_H);
    bg.fillStyle(0x1a2036, 1);
    for (const w of WALLS) {
      bg.fillRect(w.x, w.y, w.w, w.h);
    }
    bg.fillStyle(0x5cf0ff, 0.15);
    for (const v of VAULTS.filter((x) => x.team === 0)) {
      bg.fillRect(v.x, v.y, v.w, v.h);
    }
    bg.fillStyle(0xff5cab, 0.15);
    for (const v of VAULTS.filter((x) => x.team === 1)) {
      bg.fillRect(v.x, v.y, v.w, v.h);
    }
    bg.setDepth(0);

    const client = new Client(getColyseusEndpoint());
    const fighterId = this.registry.get("fighterId") ?? 0;
    try {
      this.room = await client.joinOrCreate(ROOM_NAME_ARENA, { fighterId });
    } catch (err) {
      console.error(err);
      this.add
        .text(WORLD_W / 2, WORLD_H / 2, "Server offline — start pnpm dev\nor set VITE_COLYSEUS_ENDPOINT", {
          color: "#e8ecff",
          fontSize: "16px",
          align: "center",
          fontFamily: "Space Grotesk, sans-serif",
        })
        .setOrigin(0.5)
        .setDepth(100);
      return;
    }

    this.sessionId = this.room.sessionId;
    const st = this.room.state as Record<string, unknown>;

    const players = st.players as {
      onAdd: (cb: (p: PlayerView, id: string) => void) => void;
      onRemove: (cb: (p: PlayerView, id: string) => void) => void;
      forEach: (cb: (p: PlayerView, id: string) => void) => void;
    };

    players.onAdd((player, id) => {
      const cont = this.add.container(player.x, player.y);
      const tint = getFighter(player.fighterId).tint;
      const body = this.add.circle(0, 0, PLAYER_RADIUS - 1, tint);
      const ring = this.add.circle(0, 0, PLAYER_RADIUS, 0x000000, 0);
      ring.setStrokeStyle(2, player.team === 0 ? 0x5cf0ff : 0xff5cab);
      cont.add([body, ring]);
      cont.setDepth(5);
      cont.setData("body", body);
      cont.setData("ring", ring);
      this.playerSprites.set(id, cont);
    });

    players.onRemove((_player, id) => {
      this.playerSprites.get(id)?.destroy(true);
      this.playerSprites.delete(id);
    });

    const projectiles = st.projectiles as {
      onAdd: (cb: (p: ProjView) => void) => void;
      onRemove: (cb: (p: ProjView) => void) => void;
      forEach: (cb: (p: ProjView) => void) => void;
    };

    projectiles.onAdd((pr) => {
      const c = this.add.circle(pr.x, pr.y, PROJECTILE_RADIUS, pr.isSuper ? 0xff9f1a : 0xffe066);
      c.setDepth(4);
      this.projSprites.set(pr.id, c);
    });

    projectiles.onRemove((pr) => {
      this.projSprites.get(pr.id)?.destroy();
      this.projSprites.delete(pr.id);
    });

    const pickups = st.pickups as {
      onAdd: (cb: (p: PickupView) => void) => void;
      onRemove: (cb: (p: PickupView) => void) => void;
    };

    pickups.onAdd((pk) => {
      const c = this.add.circle(pk.x, pk.y, PICKUP_RADIUS, 0x7af8a8);
      c.setStrokeStyle(2, 0xffffff, 0.35);
      c.setDepth(3);
      this.pickupSprites.set(pk.id, c);
    });

    pickups.onRemove((pk) => {
      this.pickupSprites.get(pk.id)?.destroy();
      this.pickupSprites.delete(pk.id);
    });

    const kb = this.input.keyboard!;
    this.keys = {
      w: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      a: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      s: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      d: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      e: kb.addKey(Phaser.Input.Keyboard.KeyCodes.E),
    };

    this.keys.e.on("down", () => {
      this.superEdge = true;
    });

    this.input.on("pointerdown", () => {
      this.pointerDown = true;
      this.fireEdge = true;
    });
    this.input.on("pointerup", () => {
      this.pointerDown = false;
    });

    this.input.addPointer(1);
  }

  update(_t: number, dt: number) {
    if (!this.room || !this.sessionId) return;

    const st = this.room.state as GameStateView;
    playersForEach(st.players, (p, id) => {
      const spr = this.playerSprites.get(id);
      if (!spr) return;
      spr.setPosition(p.x, p.y);
      const body = spr.getData("body") as Phaser.GameObjects.Arc;
      body.setAlpha(p.dead ? 0.35 : 1);
    });

    projectilesForEach(st.projectiles, (pr) => {
      const s = this.projSprites.get(pr.id);
      if (s) s.setPosition(pr.x, pr.y);
    });

    pickupsForEach(st.pickups, (pk) => {
      const s = this.pickupSprites.get(pk.id);
      if (s) s.setPosition(pk.x, pk.y);
    });

    this.updateHud(st);

    const me = getPlayer(st.players, this.sessionId);
    if (!me || me.dead) {
      this.sendAcc += dt;
      return;
    }

    const pointer = this.input.activePointer;
    let dx = 0;
    let dy = 0;
    if (this.input.keyboard?.enabled) {
      if (this.keys.w.isDown) dy -= 1;
      if (this.keys.s.isDown) dy += 1;
      if (this.keys.a.isDown) dx -= 1;
      if (this.keys.d.isDown) dx += 1;
    }

    if (pointer && (this.pointerDown || pointer.isDown)) {
      const w = this.scale.width;
      if (pointer.x < w * 0.42) {
        const cx = w * 0.18;
        const cy = this.scale.height * 0.78;
        const vx = pointer.x - cx;
        const vy = pointer.y - cy;
        const m = Math.hypot(vx, vy) || 1;
        const cap = 72;
        const nx = (vx / m) * Math.min(1, m / cap);
        const ny = (vy / m) * Math.min(1, m / cap);
        if (Math.abs(nx) > 0.08 || Math.abs(ny) > 0.08) {
          dx = nx;
          dy = ny;
        }
      }
    }

    const cam = this.cameras.main;
    const wx = cam.getWorldPoint(pointer.x, pointer.y).x;
    const wy = cam.getWorldPoint(pointer.x, pointer.y).y;
    const aim = Math.atan2(wy - me.y, wx - me.x);

    const fire = this.fireEdge;
    const sup = this.superEdge;
    this.fireEdge = false;
    this.superEdge = false;

    this.sendAcc += dt;
    if (this.sendAcc >= 45) {
      this.sendAcc = 0;
      this.room.send("input", {
        dx,
        dy,
        aim,
        fire,
        super: sup,
      });
    }
  }

  private updateHud(st: GameStateView) {
    const el = (id: string) => document.querySelector(`[data-testid="${id}"]`);
    el("team-score-a")!.textContent = `Team A vault: ${st.teamBanked0}`;
    el("team-score-b")!.textContent = `Team B vault: ${st.teamBanked1}`;
    const me = this.sessionId ? getPlayer(st.players, this.sessionId) : null;
    if (me) {
      el("cores-carried")!.textContent = `Cores carried: ${me.coresCarried}`;
      el("super-meter")!.textContent = `Super: ${Math.floor(me.superCharge)}%`;
      const def = getFighter(me.fighterId);
      el("ammo-readout")!.textContent = `Ammo: ${me.ammo}/${def.ammoMax}`;
    }
    if (st.matchEnded) {
      const w = st.winnerTeam;
      el("match-goal")!.textContent =
        w === 0 ? "Team A secured the cores!" : w === 1 ? "Team B secured the cores!" : "Match over";
    }
  }
}

interface PlayerView {
  x: number;
  y: number;
  fighterId: number;
  team: number;
  dead: number;
  coresCarried: number;
  superCharge: number;
  ammo: number;
}

interface ProjView {
  id: string;
  x: number;
  y: number;
  isSuper: number;
}

interface PickupView {
  id: string;
  x: number;
  y: number;
}

interface GameStateView {
  teamBanked0: number;
  teamBanked1: number;
  matchEnded: number;
  winnerTeam: number;
  players: unknown;
  projectiles: unknown;
  pickups: unknown;
}

function playersForEach(
  players: unknown,
  cb: (p: PlayerView, id: string) => void,
) {
  const m = players as Map<string, PlayerView> | undefined;
  if (m?.forEach) m.forEach((p, id) => cb(p, id));
}

function projectilesForEach(players: unknown, cb: (p: ProjView) => void) {
  const arr = players as ProjView[] | undefined;
  if (arr?.forEach) arr.forEach(cb);
}

function pickupsForEach(players: unknown, cb: (p: PickupView) => void) {
  const arr = players as PickupView[] | undefined;
  if (arr?.forEach) arr.forEach(cb);
}

function getPlayer(players: unknown, sessionId: string): PlayerView | null {
  const m = players as Map<string, PlayerView>;
  return m?.get?.(sessionId) ?? null;
}
